import { useDashboardData } from '../../hooks/useDashoardData';
import Goals from 'components/dashboards/goals';
import Grid from 'components/common/Grid';
import Skeleton from 'components/common/Skeleton';
import Map from 'components/dashboards/map';
import LinkCreator from 'components/dashboards/linkCreator';
import { useEffect, useState } from 'react';
import Content from 'components/common/Content-v2';
import { DashboardTable } from 'components/tables/dashboard';
import { BreadCrumbs } from 'components/common/BreadCrumbs';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { ActiveQuotesDashboard } from 'components/tables/quotes/ActiveQuotesDashboard';
import { OverdueQuotesDashboard } from 'components/tables/quotes/OverdueQuotesDashboard';

/* ----------  Estilos ---------- */
export const Tabs = styled.div`
	display: flex;
	margin-top: 20px;
`;

export const Tab = styled.div`
	font-size: 1.2em;
	padding: 0.5em 1em;
	border-radius: 5px 5px 0 0;
	background-color: ${props => props.theme.menuLines};
	cursor: pointer;

	&.active {
		background: ${props => props.theme.background};
	}
`;

/* ----------  Componente ---------- */
const Dashboard = () => {
	/* ------------  Estado ------------ */
	const [refresh, setRefresh] = useState(null);
	const [type, setType] = useState('active');
	const [role, setRole] = useState(localStorage.getItem('role') ?? '');
	const [selectedUser, setSelectedUser] = useState(
		localStorage.getItem('selectedUser') ?? ''
	);

	const [extraParams, setExtraParams] = useState([{ name: null, value: null }]);

	/* Hook de datos: garantiza un objeto seguro por defecto  */
	const {
		isLoading,
		response = {
			section: [],
			roleLv1: '',
			roleLv2: '',
			roleLv3: '',
			roleLv4: '',
			nameUserLv1: '',
			nameUserLv2: '',
			nameUserLv3: '',
			nameUserLv4: '',
			idLv2: '',
			idLv3: '',
		},
	} = useDashboardData(extraParams, refresh);

	const { formatMessage } = useIntl();

	/* ------------  Efectos ------------ */
	/* Construir extraParams según selección y rol */
	useEffect(() => {
		let params = [];

		if (role) params.push({ name: 'roleFilter', value: role });
		if (selectedUser) params.push({ name: 'selectedUser', value: selectedUser });
		if (params.length === 0) params.push({ name: null, value: null });

		setExtraParams(params);
		setRefresh(Date.now()); // fuerza recarga
	}, [role, selectedUser]);

	/* Ajustar rol según la respuesta del backend */
	useEffect(() => {
		const { roleLv3 = '', roleLv2 = '', roleLv1 = '' } = response;

		if (roleLv3) {
			setRole('client');
			localStorage.setItem('role', 'client');
		} else if (roleLv1 && !roleLv2) {
			setRole('');
			localStorage.setItem('role', '');
		}
	}, [response]);

	/* ------------  Helpers ------------ */
	const hasSection =
		Array.isArray(response.section) && response.section.length > 0;

	const hasQuotes =
		Array.isArray(response.section?.[0]) && response.section[0].length > 0;

	/* ------------  Render ------------ */
	return (
		<>
			{/* Estado de carga */}
			{isLoading ? (
				<Grid columns="1">
					<Skeleton count={30} />
				</Grid>
			) : (
				<>
					{/* Migas de pan */}
					<BreadCrumbs
						userLevels={{
							roleLv1: response.roleLv1,
							nameUserLv1: response.nameUserLv1,
							roleLv2: response.roleLv2,
							nameUserLv2: response.nameUserLv2,
							idLv2: response.idLv2,
							roleLv3: response.roleLv3,
							nameUserLv3: response.nameUserLv3,
							idLv3: response.idLv3,
							roleLv4: response.roleLv4,
							nameUserLv4: response.nameUserLv4,
						}}
						setSelectedUser={setSelectedUser}
						setRole={setRole}
					/>

					{/*  Resto de widgets */}
					<LinkCreator />
					<Goals {...response} role={role} />
					<Map {...response} setRole={setRole} role={role} />

					{/*  Tabla principal */}
					{hasSection && role !== 'quotes' && (
						<Content separator="2rem">
							<DashboardTable
								users={response.section}
								setRole={setRole}
								setSelectedUser={setSelectedUser}
								role={role}
							/>
						</Content>
					)}

					{/*  Tabla de cotizaciones */}
					{hasQuotes && role === 'quotes' && (
						<>
							<Tabs>
								<Tab
									className={type === 'active' ? 'active' : ''}
									onClick={() => setType('active')}>
									{formatMessage({ id: 'table.Active' })}
								</Tab>
								<Tab
									className={type === 'overdue' ? 'active' : ''}
									onClick={() => setType('overdue')}>
									{formatMessage({ id: 'table.Inactive' })}
								</Tab>
							</Tabs>

							<Content borderRadius="0 .75rem .75rem .75rem">
								{type === 'active' && (
									<ActiveQuotesDashboard
										data={response.section[0].map(doc => doc._doc)}
									/>
								)}
								{type === 'overdue' && (
									<OverdueQuotesDashboard
										data={response.section[1].map(doc => doc._doc)}
									/>
								)}
							</Content>
						</>
					)}
				</>
			)}
		</>
	);
};

export default Dashboard;
